import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import { fadeUp, stagger } from "../lib/motion";
import { SERVICES } from "../lib/site";

const BAR_DATA = [45, 64, 82, 100];
const barsWrap = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } } };
const barItem = {
  hidden: { height: "4%" },
  show: (h) => ({ height: `${h}%`, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } }),
};

const GrowthBars = () => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    variants={fadeUp}
    className="relative mb-16 overflow-hidden border border-[#2A2D34] bg-[#0A0C0F] p-6 lg:p-8"
    data-testid="services-bargraph"
  >
    <div className="mb-6 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <TrendingUp className="h-5 w-5 text-[#C6FF00]" />
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#8A939B]">Compounding Growth</span>
      </div>
      <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#C6FF00]">Month 1 → 4</span>
    </div>
    <motion.div
      variants={barsWrap}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      className="flex h-44 items-end justify-center gap-6 sm:gap-10 lg:h-56"
      aria-hidden="true"
    >
      {BAR_DATA.map((h, i) => (
        <motion.div
          key={`bar-${h}-${i}`}
          custom={h}
          variants={barItem}
          style={{ transformOrigin: "bottom" }}
          className={`w-10 rounded-t-sm bg-gradient-to-t from-[#A8F86B] via-[#D7FFB0] to-white sm:w-14 ${
            i === BAR_DATA.length - 1 ? "lime-glow" : ""
          }`}
        />
      ))}
    </motion.div>
    <div className="mt-3 h-px w-full bg-[#2A2D34]" />
    <div
      className="pointer-events-none absolute -bottom-20 -right-16 h-56 w-56 rounded-full bg-[#C6FF00]/10 blur-3xl"
      aria-hidden="true"
    />
  </motion.div>
);

export const Services = () => {
  const headingRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: headingRef, offset: ["start end", "start 35%"] });
  const scale = useTransform(scrollYProgress, [0, 1], [0.6, 1]);
  const headingOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 1]);

  return (
  <section id="services" className="relative py-28 lg:py-36 panel-grid" data-testid="services-section">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        variants={stagger}
        className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16"
      >
        <div>
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.3em] text-[#C6FF00] mb-5">
            / What We Do
          </motion.p>
          <motion.h2
            ref={headingRef}
            style={{ scale, opacity: headingOpacity, transformOrigin: "left bottom" }}
            className="text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9]"
          >
            One system.<br />Four parts.
          </motion.h2>
        </div>
        <motion.p variants={fadeUp} className="max-w-sm text-white font-medium">
          Nothing here is sold à la carte. Your website, ads, follow-up and
          local visibility are built as one HughLaunch system — each part feeds
          the next until your calendar stays full.
        </motion.p>
      </motion.div>

      <GrowthBars />

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={stagger}
        className="grid grid-cols-1 lg:grid-cols-12 gap-5"
      >
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.tag}
            variants={fadeUp}
            className={`${s.span} group glass-card relative overflow-hidden border border-white/10 p-8 lg:p-10 min-h-[240px] flex flex-col justify-between hover:border-[#C6FF00] hover:-translate-y-1.5 transition-[transform,border-color] duration-300`}
            data-testid={`service-card-${s.tag}`}
          >
            <div className={`glass-sheen ${["", "glass-sheen--d2", "glass-sheen--d3", "glass-sheen--d4"][i % 4]}`} aria-hidden="true" />
            <div
              className="glass-orb h-44 w-44 -top-14 -right-10"
              style={{ animationDelay: `${(i % 4) * 1.6}s` }}
              aria-hidden="true"
            />
            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-black text-[#C6FF00] tracking-widest">{s.tag}</span>
                <span className="rounded-full border border-[#C6FF00]/50 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#C6FF00]">
                  {s.discipline}
                </span>
              </div>
              <ArrowUpRight className="h-6 w-6 text-[#2A2D34] group-hover:text-[#C6FF00] transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
            <div className="relative">
              <h3 className="text-2xl lg:text-3xl font-black uppercase tracking-tight mb-3">
                {s.title}
              </h3>
              <p className="text-[#C7CDD4] font-medium leading-relaxed">{s.body}</p>
              <p className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-[#6B7280]">
                {s.disciplineNote}
              </p>
            </div>
            <div className="absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-[#C6FF00]/0 blur-3xl group-hover:bg-[#C6FF00]/10 transition-all duration-500" />
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
  );
};
